import { trans } from '@lingoda/i18n';
import { Box, Button, DialogHeader, Stack, Typography } from '@lingoda/ui';
import {
    Form,
    type FormHelper,
    type FormProps,
    FormWrapper,
    SelectField,
    Timezone,
} from '@lingoda/forms';
import { TimeFormat, weekStartDays, weekdays } from '@lingoda/dates';
import { capitalize } from '@lingoda/utils';
import { TimeInLocation } from './TimeInLocation';

export interface TimezoneFormValues {
    timezone: string;
    timeFormat: string | undefined;
    weekStart: number | undefined;
}

interface EditTimeZoneProps extends TimezoneFormValues {
    onConfirm: (
        values: TimezoneFormValues,
        formikHelpers: FormHelper<TimezoneFormValues>,
    ) => Promise<void>;
    onClose: () => void;
    onBack: () => void;
}

export const EditTimeZoneForm = ({
    timezone,
    timeFormat,
    weekStart,
    onConfirm,
    onClose,
    onBack,
}: EditTimeZoneProps) => {
    return (
        <FormWrapper
            onSubmit={onConfirm}
            enableReinitialize
            initialValues={{
                timezone,
                timeFormat,
                weekStart,
            }}
        >
            {(props: FormProps<TimezoneFormValues>) => (
                <Form noValidate>
                    <Stack m={{ xs: 3, sm: 4 }}>
                        <DialogHeader onClose={onClose}>
                            <Typography variant="h2">
                                {trans('edit-timezone', {}, 'student-onboarding')}
                            </Typography>
                        </DialogHeader>
                        <Stack spacing={3} my={4}>
                            <Timezone fullWidth />
                            <SelectField
                                label={trans('time-format-label', {}, 'public-student-profile')}
                                name="timeFormat"
                                fullWidth
                                options={Object.values(TimeFormat)}
                                getOptionValue={(timeFormat) => timeFormat}
                                getOptionLabel={getTranslatedTimeFormat}
                            />
                            <SelectField
                                label={trans('week-start-label', {}, 'public-student-profile')}
                                name="weekStart"
                                fullWidth
                                options={weekStartDays}
                                getOptionValue={(weekStartDay) => weekStartDay}
                                getOptionLabel={(weekStartDay) =>
                                    capitalize(weekdays(false)[weekStartDay])
                                }
                            />
                        </Stack>

                        <Box
                            mb={({ spacing }) => `clamp(${spacing(6)}, 16vh, ${spacing(28)})`}
                            textAlign="center"
                        >
                            <TimeInLocation />
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Box mr={{ xs: 1, sm: 3 }}>
                                <Button onClick={onBack} disabled={props.isSubmitting}>
                                    {trans('back-button', {}, 'student-common')}
                                </Button>
                            </Box>

                            <Button type="submit" loading={props.isSubmitting} variant="contained">
                                {trans('btn-confirm', {}, 'public-common')}
                            </Button>
                        </Box>
                    </Stack>
                </Form>
            )}
        </FormWrapper>
    );
};

const getTranslatedTimeFormat = (timeFormat: TimeFormat) => {
    switch (timeFormat) {
        case TimeFormat.Ampm:
            return trans('AM-PM-student-setting', {}, 'public-student-profile');
        case TimeFormat.Iso8601:
            return trans('24-hour-student-setting', {}, 'public-student-profile');
    }
};
